/**
 * Shared types for the HTML cleaner tool.
 *
 * The cleaner takes messy rich text (pasted from a website, an AI chat, Word,
 * Google Docs, …) and rebuilds it as clean, structural markup — then optionally
 * converts that to Markdown or plain text.
 */

/** What the user is pasting in. */
export type InputMode = 'rich' | 'html' | 'markdown';

/** What they want back out. `word` downloads a .docx built from the HTML. */
export type OutputFormat = 'html' | 'markdown' | 'text' | 'word';

export interface CleanOptions {
  // ── Attributes ────────────────────────────────────────────────────────────
  /** Drop `class` and `id`. */
  stripClassesIds: boolean;
  /** Drop `style="…"` (colours, fonts, backgrounds, spacing). */
  stripInlineStyles: boolean;
  /** Drop `data-*` attributes (tracking / framework leftovers). */
  stripDataAttrs: boolean;
  /** Drop anything not on the per-tag keep list (dir, role, aria-*, width…). */
  stripOtherAttrs: boolean;

  // ── Structure ─────────────────────────────────────────────────────────────
  /** Replace `<b>`→`<strong>`, `<i>`→`<em>`, drop `<font>`/`<center>`. */
  semanticTags: boolean;
  /** Remove `<span>`/`<font>` wrappers but keep the text inside. */
  unwrapSpans: boolean;
  /** Turn `<div>` into `<p>` when it holds text, otherwise unwrap it. */
  unwrapDivs: boolean;
  /** Delete elements left with no text and no content (empty `<p>`, `<span>`). */
  removeEmpty: boolean;
  /** Blank = keep every safe tag. Otherwise only these survive (others unwrap). */
  allowedTags: string;

  // ── Content ───────────────────────────────────────────────────────────────
  /** Unwrap `<a>` — keep the words, lose the link. */
  stripLinks: boolean;
  /** Remove `<img>`, `<picture>`, `<figure>` images. */
  stripImages: boolean;
  /** Remove `<iframe>`, `<video>`, `<audio>`, `<embed>`, `<object>`, `<svg>`. */
  stripMedia: boolean;
  /** Remove forms, inputs, buttons, selects, textareas. */
  stripFormElements: boolean;
  /** Remove tables entirely (off = keep them, cleaned). */
  stripTables: boolean;
  /** Remove `<!-- comments -->`. */
  stripComments: boolean;

  // ── Text cleanup ──────────────────────────────────────────────────────────
  /** Turn `&nbsp;` into a normal space. */
  convertNbsp: boolean;
  /** Collapse runs of spaces/newlines and trim each block. */
  collapseWhitespace: boolean;
  /** Curly quotes/apostrophes → straight, en/em dash → `-`/`--`, `…` → `...`. */
  normalizeQuotes: boolean;
  /** Drop zero-width and other invisible characters. */
  stripZeroWidth: boolean;

  // ── Output ────────────────────────────────────────────────────────────────
  format: OutputFormat;
  /** Pretty-print the HTML with indenting and one block per line. */
  tidy: boolean;
}

export const DEFAULT_OPTIONS: CleanOptions = {
  stripClassesIds: true,
  stripInlineStyles: true,
  stripDataAttrs: true,
  stripOtherAttrs: true,

  semanticTags: true,
  unwrapSpans: true,
  unwrapDivs: true,
  removeEmpty: true,
  allowedTags: '',

  stripLinks: false,
  stripImages: false,
  stripMedia: true,
  stripFormElements: true,
  stripTables: false,
  stripComments: true,

  convertNbsp: true,
  collapseWhitespace: true,
  normalizeQuotes: false,
  stripZeroWidth: true,

  format: 'html',
  tidy: true,
};
